/**
 * Notification Settings Page
 *
 * Allows users to manage their email notification preferences.
 * Includes global settings (unsubscribe all, digest toggles, day selector)
 * and per-buybox notification settings.
 *
 * Uses a Save button approach instead of auto-save.
 */

import { useState, useEffect } from 'react'
import { Link, Navigate } from '@tanstack/react-router'

import { Text } from '@/common/components/text'
import { Switch } from '@/common/components/switch'
import { Select } from '@/common/components/select'
import { Button } from '@/common/components/button'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { useToast } from '@/common/components/toast'
import { useAuth } from '@/common/hooks/useAuth'
import {
  getNotificationSettings,
  updateNotificationSettings,
  type NotificationSettingsResponse,
} from '@/features/settings/api/notificationSettings'
import {
  listBuyboxes,
  updateBuybox,
  type BuyboxResponse,
} from '@/features/buyboxes/api/buyboxes'

import * as styles from './notifications.css'

export const Route = createFileRoute({
  component: NotificationSettingsPage,
})

const WEEKDAYS = [
  { value: '0', label: 'Monday' },
  { value: '1', label: 'Tuesday' },
  { value: '2', label: 'Wednesday' },
  { value: '3', label: 'Thursday' },
  { value: '4', label: 'Friday' },
  { value: '5', label: 'Saturday' },
  { value: '6', label: 'Sunday' },
]

function NotificationSettingsPage() {
  const { token, roles } = useAuth()
  const { showSuccessToast, showErrorToast } = useToast()
  const isInvestor = roles.includes('investor')
  const isAgent = roles.includes('agent')
  const isAdmin = roles.includes('admin')

  // Original settings from server (for comparison)
  const [originalSettings, setOriginalSettings] =
    useState<NotificationSettingsResponse | null>(null)
  const [originalBuyboxes, setOriginalBuyboxes] = useState<BuyboxResponse[]>([])

  // Current local state (may have unsaved changes)
  const [settings, setSettings] = useState<NotificationSettingsResponse | null>(null)
  const [buyboxes, setBuyboxes] = useState<BuyboxResponse[]>([])

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch data on mount
  useEffect(() => {
    async function fetchData() {
      if (!token) return

      try {
        setIsLoading(true)
        setError(null)

        // Fetch notification settings
        const settingsData = await getNotificationSettings(token)
        setSettings(settingsData)
        setOriginalSettings(settingsData)

        // Fetch buyboxes only for investors
        if (isInvestor) {
          const buyboxData = await listBuyboxes(token, {
            select: [
              'id',
              'name',
              'hourly_notifications_enabled',
              'daily_notifications_enabled',
              'weekly_notifications_enabled',
            ],
            isActive: true,
          })
          setBuyboxes(buyboxData.data)
          setOriginalBuyboxes(buyboxData.data)
        }
      } catch (err) {
        console.error('Failed to fetch notification settings:', err)
        setError('Failed to load notification settings. Please try again.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [token, isInvestor])

  // Admins don't have notification settings - redirect to properties
  if (isAdmin && !isInvestor && !isAgent) {
    return <Navigate to="/properties" replace />
  }

  // Agents don't have notification settings - redirect to profile
  if (isAgent && !isAdmin) {
    return <Navigate to="/settings/profile" replace />
  }

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (!settings || !originalSettings) return false

    // Check global settings
    if (
      settings.unsubscribed_all !== originalSettings.unsubscribed_all ||
      settings.daily_digest_enabled !== originalSettings.daily_digest_enabled ||
      settings.weekly_digest_enabled !== originalSettings.weekly_digest_enabled ||
      settings.weekly_digest_day !== originalSettings.weekly_digest_day
    ) {
      return true
    }

    // Check buybox settings
    for (const buybox of buyboxes) {
      const original = originalBuyboxes.find((b) => b.id === buybox.id)
      if (!original) continue
      if (
        buybox.hourly_notifications_enabled !== original.hourly_notifications_enabled ||
        buybox.daily_notifications_enabled !== original.daily_notifications_enabled ||
        buybox.weekly_notifications_enabled !== original.weekly_notifications_enabled
      ) {
        return true
      }
    }

    return false
  }

  // Handle global settings toggle (local only)
  const handleSettingsToggle = (
    field: keyof NotificationSettingsResponse,
    value: boolean
  ) => {
    if (!settings) return
    setSettings({ ...settings, [field]: value })
  }

  // Handle weekly digest day change (local only)
  const handleDayChange = (value: string) => {
    if (!settings) return
    const dayNum = parseInt(value, 10)
    setSettings({ ...settings, weekly_digest_day: dayNum })
  }

  // Handle buybox notification toggle (local only)
  const handleBuyboxToggle = (
    buyboxId: string,
    field:
      | 'hourly_notifications_enabled'
      | 'daily_notifications_enabled'
      | 'weekly_notifications_enabled',
    value: boolean
  ) => {
    setBuyboxes(buyboxes.map((b) => (b.id === buyboxId ? { ...b, [field]: value } : b)))
  }

  // Save all changes
  const handleSave = async () => {
    if (!token || !settings) return

    setIsSaving(true)
    const errors: string[] = []

    try {
      // Save global settings if changed
      if (originalSettings) {
        const globalChanges: Partial<NotificationSettingsResponse> = {}
        if (settings.unsubscribed_all !== originalSettings.unsubscribed_all) {
          globalChanges.unsubscribed_all = settings.unsubscribed_all
        }
        if (settings.daily_digest_enabled !== originalSettings.daily_digest_enabled) {
          globalChanges.daily_digest_enabled = settings.daily_digest_enabled
        }
        if (settings.weekly_digest_enabled !== originalSettings.weekly_digest_enabled) {
          globalChanges.weekly_digest_enabled = settings.weekly_digest_enabled
        }
        if (settings.weekly_digest_day !== originalSettings.weekly_digest_day) {
          globalChanges.weekly_digest_day = settings.weekly_digest_day
        }

        if (Object.keys(globalChanges).length > 0) {
          try {
            const updated = await updateNotificationSettings(token, globalChanges)
            setSettings(updated)
            setOriginalSettings(updated)
          } catch (err) {
            console.error('Failed to update global settings:', err)
            errors.push('global settings')
          }
        }
      }

      // Save buybox changes
      for (const buybox of buyboxes) {
        const original = originalBuyboxes.find((b) => b.id === buybox.id)
        if (!original) continue

        const buyboxChanges: Record<string, boolean> = {}
        if (buybox.hourly_notifications_enabled !== original.hourly_notifications_enabled) {
          buyboxChanges.hourly_notifications_enabled =
            buybox.hourly_notifications_enabled ?? true
        }
        if (buybox.daily_notifications_enabled !== original.daily_notifications_enabled) {
          buyboxChanges.daily_notifications_enabled =
            buybox.daily_notifications_enabled ?? true
        }
        if (buybox.weekly_notifications_enabled !== original.weekly_notifications_enabled) {
          buyboxChanges.weekly_notifications_enabled =
            buybox.weekly_notifications_enabled ?? true
        }

        if (Object.keys(buyboxChanges).length > 0) {
          try {
            await updateBuybox(token, buybox.id, buyboxChanges)
          } catch (err) {
            console.error(`Failed to update buybox ${buybox.id}:`, err)
            errors.push(buybox.name)
          }
        }
      }

      // Update original buyboxes to match current state (for those that saved successfully)
      setOriginalBuyboxes([...buyboxes])

      if (errors.length === 0) {
        showSuccessToast('Settings saved successfully')
      } else {
        showErrorToast(`Failed to save: ${errors.join(', ')}`)
      }
    } catch (err) {
      console.error('Failed to save settings:', err)
      showErrorToast('Failed to save settings')
    } finally {
      setIsSaving(false)
    }
  }

  // Reset to original values
  const handleCancel = () => {
    if (originalSettings) {
      setSettings({ ...originalSettings })
    }
    if (originalBuyboxes.length > 0) {
      setBuyboxes([...originalBuyboxes])
    }
  }

  // Determine if digest options are disabled (when unsubscribed_all is true)
  const digestsDisabled = settings?.unsubscribed_all ?? false
  const isDirty = hasUnsavedChanges()

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Text as="h2" variant="bodyLg" weight="bold">
            Unable to Load Settings
          </Text>
          <Text variant="body" color="muted">
            {error}
          </Text>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text as="h1" variant="h4" weight="bold" className={styles.title}>
          Notification Settings
        </Text>
        <Text variant="body" className={styles.subtitle}>
          Manage how and when you receive email notifications.
        </Text>
      </div>

      {/* Global Settings Card */}
      <div className={styles.card}>
        <div className={styles.sectionHeader}>
          <Text as="h2" variant="bodyLg" weight="bold" className={styles.sectionTitle}>
            Email Preferences
          </Text>
          <Text variant="bodySm" className={styles.sectionDescription}>
            Control your notification email frequency.
          </Text>
        </div>

        {/* Unsubscribe All Toggle */}
        <div className={`${styles.settingRow} ${styles.settingRowFirst}`}>
          <div className={styles.settingInfo}>
            <div className={styles.settingLabel}>Unsubscribe from all emails</div>
            <div className={styles.settingDescription}>
              Stop receiving all notification emails. Transactional emails (password
              reset, etc.) will still be sent.
            </div>
          </div>
          <Switch
            checked={settings?.unsubscribed_all ?? false}
            onCheckedChange={(checked) => handleSettingsToggle('unsubscribed_all', checked)}
            disabled={isSaving}
          />
        </div>

        {/* Daily Digest Toggle */}
        <div
          className={`${styles.settingRow} ${digestsDisabled ? styles.disabledOverlay : ''}`}
        >
          <div className={styles.settingInfo}>
            <div className={styles.settingLabel}>Daily digest</div>
            <div className={styles.settingDescription}>
              Receive a daily summary of new properties matching your buyboxes, sent in
              the morning.
            </div>
          </div>
          <Switch
            checked={settings?.daily_digest_enabled ?? false}
            onCheckedChange={(checked) =>
              handleSettingsToggle('daily_digest_enabled', checked)
            }
            disabled={isSaving || digestsDisabled}
          />
        </div>

        {/* Weekly Digest Toggle */}
        <div
          className={`${styles.settingRow} ${digestsDisabled ? styles.disabledOverlay : ''}`}
        >
          <div className={styles.settingInfo}>
            <div className={styles.settingLabel}>Weekly digest</div>
            <div className={styles.settingDescription}>
              Receive a weekly summary of property activity for your buyboxes.
            </div>
            <div className={styles.daySelector}>
              <div className={styles.daySelectorSelect}>
                <Select
                  label="Send every"
                  options={WEEKDAYS}
                  value={String(settings?.weekly_digest_day ?? 0)}
                  onValueChange={handleDayChange}
                  size="sm"
                  disabled={isSaving || !settings?.weekly_digest_enabled}
                />
              </div>
            </div>
          </div>
          <Switch
            checked={settings?.weekly_digest_enabled ?? false}
            onCheckedChange={(checked) =>
              handleSettingsToggle('weekly_digest_enabled', checked)
            }
            disabled={isSaving || digestsDisabled}
          />
        </div>
      </div>

      {/* Per-Buybox Settings Card (Investors Only) */}
      {isInvestor && (
        <div className={styles.card}>
          <div className={styles.sectionHeader}>
            <Text as="h2" variant="bodyLg" weight="bold" className={styles.sectionTitle}>
              Buybox Notifications
            </Text>
            <Text variant="bodySm" className={styles.sectionDescription}>
              Control which buyboxes trigger notifications. Hourly notifications are sent
              immediately when new properties match.
            </Text>
          </div>

          {buyboxes.length === 0 ? (
            <div className={styles.emptyBuyboxes}>
              <Text variant="body" color="muted">
                You don&apos;t have any active buyboxes.{' '}
                <Link to="/buybox/create">Create one</Link> to start receiving
                property notifications.
              </Text>
            </div>
          ) : (
            <div
              className={`${styles.buyboxTable} ${digestsDisabled ? styles.disabledOverlay : ''}`}
            >
              {/* Table Header */}
              <div className={styles.buyboxTableHeader}>
                <div
                  className={`${styles.buyboxTableHeaderLabel} ${styles.buyboxTableHeaderFirst}`}
                >
                  Buybox
                </div>
                <div className={styles.buyboxTableHeaderLabel}>Hourly</div>
                <div className={styles.buyboxTableHeaderLabel}>Daily</div>
                <div className={styles.buyboxTableHeaderLabel}>Weekly</div>
              </div>

              {/* Global toggles row */}
              <div className={styles.buyboxTableGlobalRow}>
                <div className={styles.buyboxName}>All buyboxes</div>
                <div />
                <div className={styles.buyboxToggleCell}>
                  <Switch
                    size="sm"
                    checked={settings?.daily_digest_enabled ?? false}
                    onCheckedChange={(checked) =>
                      handleSettingsToggle('daily_digest_enabled', checked)
                    }
                    disabled={isSaving}
                  />
                </div>
                <div className={styles.buyboxToggleCell}>
                  <Switch
                    size="sm"
                    checked={settings?.weekly_digest_enabled ?? false}
                    onCheckedChange={(checked) =>
                      handleSettingsToggle('weekly_digest_enabled', checked)
                    }
                    disabled={isSaving}
                  />
                </div>
              </div>

              {/* Table Rows */}
              {buyboxes.map((buybox) => (
                <div key={buybox.id} className={styles.buyboxTableRow}>
                  <div className={styles.buyboxName}>{buybox.name}</div>
                  <div className={styles.buyboxToggleCell}>
                    <Switch
                      size="sm"
                      checked={buybox.hourly_notifications_enabled ?? true}
                      onCheckedChange={(checked) =>
                        handleBuyboxToggle(buybox.id, 'hourly_notifications_enabled', checked)
                      }
                      disabled={isSaving}
                    />
                  </div>
                  <div
                    className={`${styles.buyboxToggleCell} ${!settings?.daily_digest_enabled ? styles.buyboxToggleCellDisabled : ''}`}
                  >
                    <Switch
                      size="sm"
                      checked={buybox.daily_notifications_enabled ?? true}
                      onCheckedChange={(checked) =>
                        handleBuyboxToggle(buybox.id, 'daily_notifications_enabled', checked)
                      }
                      disabled={isSaving || !settings?.daily_digest_enabled}
                    />
                  </div>
                  <div
                    className={`${styles.buyboxToggleCell} ${!settings?.weekly_digest_enabled ? styles.buyboxToggleCellDisabled : ''}`}
                  >
                    <Switch
                      size="sm"
                      checked={buybox.weekly_notifications_enabled ?? true}
                      onCheckedChange={(checked) =>
                        handleBuyboxToggle(buybox.id, 'weekly_notifications_enabled', checked)
                      }
                      disabled={isSaving || !settings?.weekly_digest_enabled}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Save/Cancel buttons */}
      <div className={styles.actions}>
        <Button variant="outline" onClick={handleCancel} disabled={!isDirty || isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!isDirty || isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  )
}
